import { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react'
import { useUser } from '@clerk/clerk-react'
import { useStore } from './store/useStore'
import { Helmet } from 'react-helmet-async'
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels'
import Header from './components/Header'
import MobileHeader from './components/MobileHeader'
import MobileLayout from './components/MobileLayout'
import FileTree from './components/FileTree'
import Preview from './components/Preview'
import StatusBar from './components/StatusBar'
import { Toaster, showToast } from './components/ui/Toast'
import { PanelLeft, Terminal, Loader } from 'lucide-react'
import { useIsMobileOrTablet, useIsTablet } from './hooks/useMediaQuery'
import { haptic } from './utils/haptics'
import { registerServiceWorker } from './utils/pushNotifications'
import NotificationPrompt from './components/NotificationPrompt'
import CollaborationProvider from './components/CollaborationProvider'

// Lazy load heavy components
const Editor = lazy(() => import('./components/Editor'))
const AiAssistantSimple = lazy(() => import('./components/AiAssistantSimple'))
const EnhancedDebugConsole = lazy(() => import('./components/EnhancedDebugConsole'))
const AuthModal = lazy(() => import('./components/AuthModal'))
const About = lazy(() => import('./components/About'))
const Changelog = lazy(() => import('./components/Changelog'))
const SketchBrowser = lazy(() => import('./components/SketchBrowser'))
const ResearchSurvey = lazy(() => import('./components/ResearchSurvey'))
const CommandPalette = lazy(() => import('./components/CommandPalette'))
const LandingModal = lazy(() => import('./components/LandingModal'))
import OnboardingTour, { resetOnboardingTour } from './components/OnboardingTour'

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-full bg-ide-panel text-ide-textMuted">
      <Loader className="animate-spin mr-2" size={20} />
      <span>Loading...</span>
    </div>
  )
}

function App() {
  const { isSignedIn, isLoaded: isClerkLoaded } = useUser()
  const { isAuthenticated, showAiPanel, showConsole, shouldShowSurvey, clearSurveyTrigger } = useStore()
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [showAbout, setShowAbout] = useState(false)
  const [showFileTree, setShowFileTree] = useState(true)
  const [showBrowser, setShowBrowser] = useState(false)
  const [showSurvey, setShowSurvey] = useState(false)
  const [showCommandPalette, setShowCommandPalette] = useState(false)
  const [showChangelog, setShowChangelog] = useState(false)
  const [showTour, setShowTour] = useState(false)
  const [showLandingModal, setShowLandingModal] = useState(false)
  const [isGuestMode, setIsGuestMode] = useState(false)
  const isMobileOrTablet = useIsMobileOrTablet()
  const isTablet = useIsTablet()
  
  // Show landing modal on first visit if not in guest mode
  // Does NOT wait for Clerk to load - auth happens on primary domain (xelsed.ai)
  useEffect(() => {
    try {
      const hasVisited = localStorage.getItem('p5ai_has_visited')
      const guestMode = localStorage.getItem('p5ai_guest_mode')
      
      if (guestMode === 'true') {
        setIsGuestMode(true)
        return
      }
      
      if (!hasVisited) {
        setShowLandingModal(true)
      }
    } catch (e) {
      // localStorage blocked, show landing modal
      setShowLandingModal(true)
    }
  }, []) // Run once on mount, no Clerk dependency
  
  // Close landing modal if user signs in (Clerk loads and detects signed in state)
  useEffect(() => {
    if (isClerkLoaded && isSignedIn) {
      setShowLandingModal(false)
    }
  }, [isClerkLoaded, isSignedIn])
  
  const handleContinueAsGuest = () => {
    setIsGuestMode(true)
    setShowLandingModal(false)
    try {
      localStorage.setItem('p5ai_guest_mode', 'true')
      localStorage.setItem('p5ai_has_visited', 'true')
    } catch (e) {}
  }
  
  // Show dyslexia survey after Clerk sign-in if not completed
  useEffect(() => {
    if (!isClerkLoaded || !isSignedIn) return
    
    // Close landing modal if user just signed in
    setShowLandingModal(false)
    
    try {
      const profile = localStorage.getItem('p5ai_research_profile_v1')
      if (profile) {
        const data = JSON.parse(profile)
        if (data.answered === true) {
          return // Already completed survey
        }
      }
      // User signed in but hasn't completed survey - show it
      setShowSurvey(true)
    } catch (e) {
      // localStorage blocked, show survey anyway
      setShowSurvey(true)
    }
  }, [isClerkLoaded, isSignedIn])
  
  // Ref to track if session tracking is set up (to avoid duplicate listeners)
  const sessionTrackingCleanupRef = useRef(null)
  
  // Register service worker for push notifications on app load
  useEffect(() => {
    registerServiceWorker().then(reg => {
      if (reg) console.log('[App] Service worker registered')
    }).catch(err => {
      console.warn('[App] Service worker registration failed:', err)
    })
  }, [])
  
  // Helper function to set up session tracking for a participant
  // This handles: setting store state, creating session, and attaching beforeunload
  const setupSessionTracking = useCallback((participantId) => {
    if (!participantId) return
    
    // Clean up any existing listener first
    if (sessionTrackingCleanupRef.current) {
      sessionTrackingCleanupRef.current()
      sessionTrackingCleanupRef.current = null
    }
    
    // Set participant in store
    useStore.getState().setResearchParticipant(participantId)
    
    // Generate new session ID for this visit
    const sessionId = crypto.randomUUID()
    useStore.getState().setResearchSession(sessionId)
    
    // Track session end on page unload (browser close, refresh, navigate away)
    const handleUnload = () => {
      const currentSessionId = useStore.getState().sessionId
      if (currentSessionId) {
        // Best-effort beacon to log session end
        const blob = new Blob([JSON.stringify({
          sessionId: currentSessionId,
          endedReason: 'page_unload',
          timestamp: new Date().toISOString()
        })], { type: 'application/json' })
        navigator.sendBeacon('/api/research-session-end', blob)
      }
    }
    
    // Attach the handler
    window.addEventListener('beforeunload', handleUnload)
    
    // Store cleanup function
    sessionTrackingCleanupRef.current = () => {
      window.removeEventListener('beforeunload', handleUnload)
    }
    
    console.log('[Research] Session tracking initialized:', { participantId, sessionId })
  }, [])

  // Global console message bridge - always listening regardless of console visibility
  useEffect(() => {
    const handleConsoleMessage = (event) => {
      // Only handle console messages from iframe
      if (event.data && event.data.type === 'console') {
        // Forward to store for persistent buffering
        useStore.getState().addConsoleMessage(event.data)
      }
    }
    
    window.addEventListener('message', handleConsoleMessage)
    return () => window.removeEventListener('message', handleConsoleMessage)
  }, []) // Empty deps - always active

  useEffect(() => {
    // Check for stored session - gracefully handle Safari tracking prevention
    try {
      const storedSession = localStorage.getItem('p5ai_session')
      if (storedSession) {
        const session = JSON.parse(storedSession)
        useStore.getState().setUser(session.user)
        useStore.getState().setP5Credentials(session.p5Credentials)
      }
    } catch (error) {
      // Silent fail - Safari tracking prevention or storage quota exceeded
      // App works fine without restored session
    }

    // Check for research profile - show survey on first visit
    let hasCompletedSurvey = false
    let existingParticipantId = null
    
    try {
      const profile = localStorage.getItem('p5ai_research_profile_v1')
      if (profile) {
        const data = JSON.parse(profile)
        
        if (data.participantId) {
          existingParticipantId = data.participantId
          
          // Only hide survey if they've actually completed it (answered: true)
          if (data.answered === true) {
            hasCompletedSurvey = true
          }
        }
      }
    } catch (error) {
      // Silent fail - will show survey
    }
    
    // Set up tracking for returning participants (completed OR skipped)
    if (existingParticipantId) {
      setupSessionTracking(existingParticipantId)
    }
    
    // Don't show survey on initial load - wait for first AI response
    
    // Cleanup on unmount
    return () => {
      if (sessionTrackingCleanupRef.current) {
        sessionTrackingCleanupRef.current()
      }
    }
  }, [setupSessionTracking])

  // Watch for survey trigger from AI assistant (after first response)
  useEffect(() => {
    if (shouldShowSurvey) {
      // Check if already completed
      let hasCompletedSurvey = false
      try {
        const profile = localStorage.getItem('p5ai_research_profile_v1')
        if (profile) {
          const data = JSON.parse(profile)
          hasCompletedSurvey = data.answered === true
        }
      } catch (e) {}
      
      if (!hasCompletedSurvey) {
        setShowSurvey(true)
      }
      clearSurveyTrigger()
    }
  }, [shouldShowSurvey, clearSurveyTrigger])

  // Check for remix URL parameter on load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const remixId = urlParams.get('remix')
    
    if (remixId) {
      // Clear the URL parameter without reload
      window.history.replaceState({}, '', window.location.pathname)
      
      // Fetch and load the remix sketch
      loadRemixSketch(remixId)
    }
  }, [])

  // Load remix sketch from gallery
  const loadRemixSketch = async (sketchId) => {
    try {
      showToast('Loading sketch to remix...', 'info')
      
      const response = await fetch(`/api/gallery-remix?id=${sketchId}`)
      const data = await response.json()
      
      if (!data.success) {
        showToast(data.error || 'Failed to load sketch', 'error')
        return
      }
      
      const { sketch } = data
      
      // Check if index.html uses external file references (new format) or inline code (old format)
      // New format: <script src="sketch.js"></script>
      // Old format: <script>...inline code...</script>
      const isNewFormat = sketch.indexContent && sketch.indexContent.includes('src="sketch.js"')
      
      let finalIndexHtml, finalStyleCss, finalSketchCode
      
      if (isNewFormat) {
        // NEW FORMAT: Use files directly from R2 - no parsing needed
        finalIndexHtml = sketch.indexContent
        finalStyleCss = sketch.styleContent || `* { margin: 0; padding: 0; box-sizing: border-box; }
html, body { overflow: hidden; height: 100%; background: #1a1a2e; }
canvas { display: block; }`
        finalSketchCode = sketch.code
      } else {
        // OLD FORMAT: Parse index.html to extract libraries, use stored sketch.js and style.css
        let libraryScripts = []
        let extractedStyles = ''
        
        if (sketch.indexContent) {
          try {
            const parser = new DOMParser()
            const doc = parser.parseFromString(sketch.indexContent, 'text/html')
            
            // Extract external script tags (libraries like p5.js, dat.gui, etc.)
            const scripts = doc.querySelectorAll('script[src]')
            scripts.forEach(script => {
              const src = script.getAttribute('src')
              if (src && (src.startsWith('http') || src.startsWith('//'))) {
                libraryScripts.push(src)
              }
            })
            
            // Extract inline styles from <style> tags
            const styleTags = doc.querySelectorAll('style')
            styleTags.forEach(style => {
              extractedStyles += style.textContent + '\n'
            })
          } catch (e) {
            console.warn('[Remix] Failed to parse index.html:', e)
          }
        }
        
        // Default p5.js libraries if none were extracted
        if (libraryScripts.length === 0) {
          const p5Ver = sketch.p5Version || '1.11.11'
          libraryScripts = [
            `https://cdn.jsdelivr.net/npm/p5@${p5Ver}/lib/p5.min.js`,
            `https://cdn.jsdelivr.net/npm/p5@${p5Ver}/lib/addons/p5.sound.min.js`
          ]
        }
        
        // Build clean index.html with external file references
        const scriptTags = libraryScripts.map(src => `  <script src="${src}"></script>`).join('\n')
        finalIndexHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${sketch.title}</title>
${scriptTags}
  <link rel="stylesheet" href="style.css">
</head>
<body>
  <script src="sketch.js"></script>
</body>
</html>`
        
        // Use stored style.css, extracted styles, or default
        const defaultStyleCss = `* { margin: 0; padding: 0; box-sizing: border-box; }
html, body { overflow: hidden; height: 100%; background: #1a1a2e; }
canvas { display: block; }`
        finalStyleCss = sketch.styleContent || extractedStyles.trim() || defaultStyleCss
        
        // Use stored sketch.js (the original code)
        finalSketchCode = sketch.code
      }

      // Create new files with remixed code
      const newFiles = [
        {
          id: 'sketch-' + Date.now(),
          name: 'sketch.js',
          content: finalSketchCode,
          type: 'javascript'
        },
        {
          id: 'index-' + Date.now(),
          name: 'index.html',
          content: finalIndexHtml,
          type: 'html'
        },
        {
          id: 'style-' + Date.now(),
          name: 'style.css',
          content: finalStyleCss,
          type: 'css'
        }
      ]
      
      // Update store
      useStore.getState().setFiles(newFiles)
      useStore.getState().setSketchName(sketch.title)
      useStore.getState().setSaved(false)
      
      if (sketch.p5Version) {
        useStore.getState().setP5Version(sketch.p5Version)
      }
      
      haptic('success')
      showToast(`Remixing "${sketch.originalTitle}" by ${sketch.author}`, 'success')
      
    } catch (error) {
      console.error('[Remix] Error loading sketch:', error)
      showToast('Failed to load sketch for remixing', 'error')
    }
  }

  // Command palette keyboard shortcut (Ctrl+K / Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        setShowCommandPalette(prev => !prev)
      }
    }

    const handleOpenPalette = () => setShowCommandPalette(true)
    const handleToggleFileTree = () => setShowFileTree(prev => !prev)

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('openCommandPalette', handleOpenPalette)
    window.addEventListener('toggleFileTree', handleToggleFileTree)
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('openCommandPalette', handleOpenPalette)
      window.removeEventListener('toggleFileTree', handleToggleFileTree)
    }
  }, [])

  return (
    <div className="h-dvh flex flex-col bg-ide-bg text-ide-text">
      <Helmet>
        <title>P5 AI Studio | Free Vibe Coding with GPT-5.1, Claude & Gemini for p5.js</title>
        <meta name="description" content="FREE vibe coding with GPT-5.1, Claude 3.5, Gemini 3.0 & Codex. Create p5.js generative art and creative coding projects with AI. Research-backed platform studying how people learn to code. No API keys needed." />
        <meta property="og:title" content="P5 AI Studio - Free Vibe Coding & Creative Coding with AI" />
        <meta property="og:description" content="Free vibe coding with GPT-5.1, Claude & Gemini. Create p5.js generative art with AI. Research platform studying how people learn creative coding." />
        <meta property="og:url" content="https://p5js.ai/" />
      </Helmet>

      {/* Conditional Header - Use mobile/tablet header for touch devices */}
      {isMobileOrTablet ? (
        <MobileHeader 
          onAuthClick={() => setShowAuthModal(true)}
          onAboutClick={() => setShowAbout(true)}
          onChangelogClick={() => setShowChangelog(true)}
          onBrowseClick={() => setShowBrowser(true)}
          isTablet={isTablet}
        />
      ) : (
        <Header 
          onAuthClick={() => setShowAuthModal(true)}
          onAboutClick={() => setShowAbout(true)}
          onChangelogClick={() => setShowChangelog(true)}
          onHelpClick={() => {
            resetOnboardingTour()
            setShowTour(true)
          }}
        />
      )}
      
      <main id="main-content" className="flex-1 overflow-hidden">
        {/* Mobile/Tablet Layout - Touch-friendly for phones and iPads */}
        {isMobileOrTablet ? (
          <MobileLayout 
            onAuthClick={() => setShowAuthModal(true)}
            onAboutClick={() => setShowAbout(true)}
            onChangelogClick={() => setShowChangelog(true)}
            isTablet={isTablet}
          />
        ) : (
          /* Desktop Layout */
          <PanelGroup direction="horizontal" className="h-full">
          {/* File Tree Panel */}
          {showFileTree && (
            <>
              <Panel defaultSize={15} minSize={12} maxSize={30} className="p-1.5">
                <div className="h-full bg-ide-panel rounded-lg border border-ide-border overflow-hidden">
                  <FileTree onHide={() => setShowFileTree(false)} />
                </div>
              </Panel>
              <PanelResizeHandle className="w-1 bg-transparent hover:bg-ide-accent transition-colors" />
            </>
          )}
          
          {/* Editor Panel */}
          <Panel defaultSize={35} minSize={20} className="p-1.5">
            <div className="h-full bg-ide-panel rounded-lg border border-ide-border overflow-hidden" data-tour="editor">
              <Suspense fallback={<LoadingSpinner />}>
                <Editor />
              </Suspense>
            </div>
          </Panel>
          
          <PanelResizeHandle className="w-1 bg-transparent hover:bg-ide-accent transition-colors" />
          
          {/* Preview + Console Panel (Vertical Stack) */}
          <Panel defaultSize={30} minSize={20} className="p-1.5">
            <PanelGroup direction="vertical" className="h-full">
              {/* Preview Panel */}
              <Panel defaultSize={showConsole ? 60 : 100} minSize={30}>
                <div className="h-full bg-ide-panel rounded-lg border border-ide-border overflow-hidden" data-tour="preview">
                  <Preview />
                </div>
              </Panel>
              
              {/* Console Panel - Below Preview Only */}
              {showConsole && (
                <>
                  <PanelResizeHandle className="h-1 bg-transparent hover:bg-ide-accent transition-colors my-1" />
                  <Panel defaultSize={40} minSize={20} maxSize={70}>
                    <div className="h-full bg-ide-panel rounded-lg border border-ide-border overflow-hidden" data-tour="console">
                      <Suspense fallback={<LoadingSpinner />}>
                        <EnhancedDebugConsole />
                      </Suspense>
                    </div>
                  </Panel>
                </>
              )}
            </PanelGroup>
          </Panel>
          
          {/* AI Assistant Panel */}
          {showAiPanel && (
            <>
              <PanelResizeHandle className="w-1 bg-transparent hover:bg-ide-accent transition-colors" />
              <Panel defaultSize={20} minSize={15} maxSize={40} className="p-1.5">
                <div className="h-full bg-ide-panel rounded-lg border border-ide-border overflow-hidden" data-tour="ai-panel">
                  <Suspense fallback={<LoadingSpinner />}>
                    <AiAssistantSimple />
                  </Suspense>
                </div>
              </Panel>
            </>
          )}
          </PanelGroup>
        )}
        
        {/* Floating buttons for collapsed panels (desktop only) */}
        {!isMobileOrTablet && !showFileTree && (
          <button
            onClick={() => setShowFileTree(true)}
            className="fixed left-2 top-16 z-10 p-2 bg-ide-surface text-ide-textMuted hover:bg-ide-surfaceHover hover:text-ide-text rounded-lg border border-ide-border transition-all active:scale-95"
            title="Show File Tree"
            aria-label="Show File Tree"
          >
            <PanelLeft size={18} />
          </button>
        )}
        
        {!isMobileOrTablet && !showConsole && (
          <button
            onClick={() => useStore.getState().toggleConsole()}
            className="fixed bottom-2 left-2 z-10 p-2 bg-ide-surface text-ide-textMuted hover:bg-ide-surfaceHover hover:text-ide-text rounded-lg border border-ide-border transition-all active:scale-95"
            title="Show Console"
            aria-label="Show Console"
          >
            <Terminal size={18} />
          </button>
        )}
      </main>

      {/* Status Bar - Desktop only */}
      {!isMobileOrTablet && <StatusBar />}

      {showAuthModal && (
        <Suspense fallback={null}>
          <AuthModal onClose={() => setShowAuthModal(false)} />
        </Suspense>
      )}

      <Suspense fallback={null}>
        <About isOpen={showAbout} onClose={() => setShowAbout(false)} />
      </Suspense>

      <Suspense fallback={null}>
        <Changelog isOpen={showChangelog} onClose={() => setShowChangelog(false)} />
      </Suspense>

      {showBrowser && (
        <Suspense fallback={null}>
          <SketchBrowser onClose={() => setShowBrowser(false)} />
        </Suspense>
      )}

      {showSurvey && (
        <Suspense fallback={null}>
          <ResearchSurvey
            onComplete={(participantId) => {
              setupSessionTracking(participantId)  // Sets up participant, session, AND beforeunload
              setShowSurvey(false)
            }}
            onSkip={(participantId) => {
              // participantId is passed from ResearchSurvey after creating skipped profile
              if (participantId) {
                setupSessionTracking(participantId)  // Sets up tracking even for skipped users
              }
              setShowSurvey(false)
            }}
          />
        </Suspense>
      )}

      <Toaster />

      {/* Collaboration Provider - manages PartyKit WebSocket for social coding */}
      <CollaborationProvider />

      {/* Landing Modal - Guest vs Login choice */}
      {showLandingModal && (
        <Suspense fallback={null}>
          <LandingModal 
            onContinueAsGuest={handleContinueAsGuest}
            onClose={() => {
              setShowLandingModal(false)
              try {
                localStorage.setItem('p5ai_has_visited', 'true')
              } catch (e) {}
            }}
          />
        </Suspense>
      )}

      {/* Command Palette */}
      {showCommandPalette && (
        <Suspense fallback={null}>
          <CommandPalette
            onClose={() => setShowCommandPalette(false)}
            onAuthClick={() => {
              setShowCommandPalette(false)
              setShowAuthModal(true)
            }}
            onAboutClick={() => {
              setShowCommandPalette(false)
              setShowAbout(true)
            }}
            onChangelogClick={() => {
              setShowCommandPalette(false)
              setShowChangelog(true)
            }}
            onBrowseClick={() => {
              setShowCommandPalette(false)
              setShowBrowser(true)
            }}
          />
        </Suspense>
      )}

      {/* Push Notification Prompt */}
      <NotificationPrompt />

      {/* Onboarding Tour (desktop only) */}
      {!isMobileOrTablet && (
        <OnboardingTour 
          forceShow={showTour} 
          onComplete={() => setShowTour(false)} 
        />
      )}
    </div>
  )
}

export default App
